import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRecentSports } from "@/hooks/useRecentSports";
import { sportsAtom } from "@/state/filters.state";
import type { SportType } from "@/types/data.types";
import { defaultSections, sportIcon, sportLabel } from "@/types/sports.meta";
import { isSportChecked, toggleSport } from "@/utils/filters.utils";
import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useAtom } from "jotai";
import { useMemo } from "react";

type SportsPickerProps = {
  onDone?: () => void;
};

export function SportsPicker({ onDone }: SportsPickerProps) {
  const [sportsFilter, setSportsFilter] = useAtom(sportsAtom);
  const { i18n } = useLingui();

  const { data: recentSports, isLoading: recentSportsLoading } =
    useRecentSports({ months: 6, limit: 10 });

  const sections = useMemo(() => {
    const highlighted =
      !recentSportsLoading && recentSports?.sports?.length
        ? [{ title: msg`Highlighted`, sports: recentSports.sports }]
        : [];
    return [...highlighted, ...defaultSections].filter(
      (sec) => sec.sports.length > 0,
    );
  }, [recentSportsLoading, recentSports]);

  function toggleSportFilter(s: SportType) {
    setSportsFilter((prev) => toggleSport(prev, s));
  }

  const allSelected = sportsFilter.mode === "all";

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background">
        <Button
          type="button"
          variant={allSelected ? "secondary" : "ghost"}
          className="w-full justify-start"
          onClick={() => {
            if (allSelected) {
              setSportsFilter({ mode: "include", sports: [] });
            } else {
              setSportsFilter({ mode: "all" });
            }
          }}
        >
          {t`All sports`}
        </Button>

        <Separator className="my-2" />
      </div>

      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]">
        {sections.map((sec) => {
          const secTitle = i18n._(sec.title);
          return (
            <div key={secTitle} className="mb-2">
              <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                {secTitle}
              </div>

              <div className="space-y-0.5">
                {sec.sports.map((s) => {
                  const checked = isSportChecked(sportsFilter, s);
                  const Icon = sportIcon(s);

                  return (
                    <label
                      key={`${secTitle}-${s}`}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSportFilter(s)}
                      />
                      <Icon className="h-4 w-4" />
                      <span className="min-w-0 truncate">
                        {i18n._(sportLabel(s))}
                      </span>
                    </label>
                  );
                })}
              </div>

              <Separator className="mt-2" />
            </div>
          );
        })}
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 z-10 bg-background pt-3 pb-[var(--safe-bottom)]">
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setSportsFilter({ mode: "include", sports: [] })}
            className="flex-1"
          >
            {t`Clear`}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onDone}
            className="flex-1"
          >
            {t`Done`}
          </Button>
        </div>
      </div>
    </div>
  );
}
